// AdminDashboard.js
import React, { useEffect, useState } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { db, storage } from "../firebaseConfig";
import {
    collection,
    doc,
    onSnapshot,
    setDoc,
    addDoc,
    query,
    orderBy,
    limit,
    deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./SimulacrumExplorerStyle.css";

const ADMIN_WALLETS = [
    // your admin wallet(s)
    "5Je5czeRdZ6WnbjQDhK8C6vNnYowh7TjchxAE3AyBhZL",
    "8pDhQTo9JEXoWQ87B2G8AeR6i9uA1TeXsUwb7MRdvDiM",
];

function AdminDashboard() {
    const { publicKey } = useWallet();

    const [styles, setStyles] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [statusMessage, setStatusMessage] = useState("");

    const [form, setForm] = useState({
        id: "",
        name: "",
        themeKey: "",
        priceSol: "0.1",
        previewImageUrl: "",
        description: "",
        active: true,
        screenshotUrls: [], // ⭐ NEW
    });

    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const [uploadingScreenshots, setUploadingScreenshots] = useState(false); // ⭐ NEW

    const isAdmin =
        publicKey && ADMIN_WALLETS.includes(publicKey.toBase58());

    useEffect(() => {
        if (!isAdmin) return;

        const stylesRef = collection(db, "styles_master");
        const unsubStyles = onSnapshot(stylesRef, (snap) => {
            const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setStyles(items);
        });

        const txRef = collection(db, "style_purchases");
        const txQuery = query(txRef, orderBy("createdAt", "desc"), limit(50));
        const unsubTx = onSnapshot(txQuery, (snap) => {
            const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setTransactions(items);
        });

        return () => {
            unsubStyles();
            unsubTx();
        };
    }, [isAdmin]);

    if (!publicKey) return <p>Please connect your wallet to view admin tools.</p>;
    if (!isAdmin) return <p>You are not authorized to view this page.</p>;

    // ---------- IMAGE UPLOAD (preview) ----------
    const handleImageFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!form.id) {
            setStatusMessage("❌ Please set a Style ID before uploading an image.");
            e.target.value = "";
            return;
        }

        try {
            setUploadingImage(true);
            setUploadProgress(0);
            setStatusMessage("📤 Uploading preview image…");

            const safeId = form.id.replace(/[^a-zA-Z0-9_-]/g, "_");
            const storagePath = `style-skins/${safeId}/${Date.now()}-${file.name}`;
            const fileRef = ref(storage, storagePath);

            const snapshot = await uploadBytes(fileRef, file);
            setUploadProgress(100);

            const downloadUrl = await getDownloadURL(snapshot.ref);

            setForm((prev) => ({
                ...prev,
                previewImageUrl: downloadUrl,
            }));

            setStatusMessage("✅ Preview image uploaded and URL set.");
        } catch (err) {
            console.error("Error uploading image:", err);
            setStatusMessage("❌ Failed to upload preview image. Check console.");
        } finally {
            setUploadingImage(false);
        }
    };

    // ---------- SCREENSHOT UPLOADS (one or many) ----------
    const handleScreenshotFilesChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        if (!form.id) {
            setStatusMessage("❌ Please set a Style ID before uploading screenshots.");
            e.target.value = "";
            return;
        }

        try {
            setUploadingScreenshots(true);
            setStatusMessage("📸 Uploading screenshot(s)…");

            const safeId = form.id.replace(/[^a-zA-Z0-9_-]/g, "_");
            const newUrls = [];

            for (const file of files) {
                const storagePath = `style-skins/${safeId}/screenshots/${Date.now()}-${file.name}`;
                const fileRef = ref(storage, storagePath);
                const snapshot = await uploadBytes(fileRef, file);
                const url = await getDownloadURL(snapshot.ref);
                newUrls.push(url);
            }

            setForm((prev) => ({
                ...prev,
                screenshotUrls: [...(prev.screenshotUrls || []), ...newUrls],
            }));

            setStatusMessage("✅ Screenshot(s) uploaded and added to this style.");
        } catch (err) {
            console.error("Error uploading screenshots:", err);
            setStatusMessage("❌ Failed to upload screenshots. Check console.");
        } finally {
            setUploadingScreenshots(false);
            e.target.value = "";
        }
    };

    const handleRemoveScreenshot = (urlToRemove) => {
        setForm((prev) => ({
            ...prev,
            screenshotUrls: (prev.screenshotUrls || []).filter(
                (url) => url !== urlToRemove
            ),
        }));
        setStatusMessage(
            "ℹ️ Screenshot removed from this style (file remains in Storage)."
        );
    };

    // ---------- FORM CHANGE ----------
    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleResetForm = () => {
        setForm({
            id: "",
            name: "",
            themeKey: "",
            priceSol: "0.1",
            previewImageUrl: "",
            description: "",
            active: true,
            screenshotUrls: [],
        });
        setStatusMessage("ℹ️ Form cleared.");
    };

    // ---------- SAVE STYLE ----------
    const handleSaveStyle = async (e) => {
        e.preventDefault();

        if (!form.id || !form.themeKey) {
            setStatusMessage("❌ Style ID and Theme Key are required.");
            return;
        }

        const priceLamports = Math.floor(
            parseFloat(form.priceSol || "0") * 1_000_000_000
        );

        try {
            await setDoc(doc(db, "styles_master", form.id), {
                name: form.name || form.id,
                themeKey: form.themeKey,
                priceLamports,
                previewImageUrl: form.previewImageUrl || null,
                description: form.description || "",
                active: form.active,
                screenshotUrls: form.screenshotUrls || [], // ⭐ NEW
                updatedAt: new Date(),
            });

            setStatusMessage(`✅ Style "${form.id}" saved successfully.`);
        } catch (err) {
            console.error(err);
            setStatusMessage("❌ Failed to save style.");
        }
    };

    // ---------- EDIT STYLE ----------
    const handleEditStyle = (style) => {
        const priceSol =
            typeof style.priceLamports === "number"
                ? (style.priceLamports / 1_000_000_000).toString()
                : style.priceSol || "0.1";

        setForm({
            id: style.id,
            name: style.name || style.id,
            themeKey: style.themeKey || "",
            priceSol,
            previewImageUrl: style.previewImageUrl || "",
            description: style.description || "",
            active: style.active ?? true,
            screenshotUrls: style.screenshotUrls || [], // ⭐ NEW
        });

        setStatusMessage(`📝 Editing style "${style.id}".`);
    };

    // ---------- DELETE STYLE ----------
    const handleDeleteStyle = async (styleId) => {
        const confirmed = window.confirm(
            `Delete style "${styleId}"? This removes it from the shop but not from past purchases.`
        );
        if (!confirmed) return;

        try {
            await deleteDoc(doc(db, "styles_master", styleId));

            setStatusMessage(`🗑️ Style "${styleId}" deleted.`);

            if (form.id === styleId) handleResetForm();
        } catch (err) {
            console.error("Error deleting style:", err);
            setStatusMessage("❌ Failed to delete style.");
        }
    };

    return (
        <div className="wallet-explorer-root theme-og-retro">
            <h2>Style Admin Dashboard</h2>

            {/* STATUS SECTION */}
            {statusMessage && (
                <div
                    style={{
                        padding: "10px",
                        background: "#222",
                        color: "white",
                        borderRadius: "6px",
                        marginBottom: "15px",
                        border: "1px solid #555",
                    }}
                >
                    {statusMessage}
                </div>
            )}

            {/* FORM */}
            <form onSubmit={handleSaveStyle}>
                <h3>Create / Edit Style</h3>

                <label>
                    Style ID (doc id)
                    <input
                        type="text"
                        name="id"
                        value={form.id}
                        onChange={handleFormChange}
                    />
                </label>

                <label>
                    Display Name
                    <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleFormChange}
                    />
                </label>

                <label>
                    Theme Key (CSS)
                    <input
                        type="text"
                        name="themeKey"
                        value={form.themeKey}
                        onChange={handleFormChange}
                    />
                </label>

                <label>
                    Price (SOL)
                    <input
                        type="number"
                        step="0.001"
                        name="priceSol"
                        value={form.priceSol}
                        onChange={handleFormChange}
                    />
                </label>

                <label>
                    Preview Image URL
                    <input
                        type="text"
                        name="previewImageUrl"
                        value={form.previewImageUrl}
                        onChange={handleFormChange}
                        placeholder="Auto-filled when uploading"
                    />
                </label>

                <label>
                    Upload Preview Image
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageFileChange}
                        disabled={uploadingImage}
                    />
                </label>
                {uploadingImage && (
                    <p>Uploading preview image… {uploadProgress ?? ""}</p>
                )}

                {/* SCREENSHOT UPLOADS */}
                <label>
                    Upload Screenshots
                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleScreenshotFilesChange}
                        disabled={uploadingScreenshots}
                    />
                </label>
                {uploadingScreenshots && <p>Uploading screenshots…</p>}

                {form.screenshotUrls && form.screenshotUrls.length > 0 && (
                    <div className="style-screenshot-strip">
                        {form.screenshotUrls.map((url) => (
                            <div
                                key={url}
                                className="style-screenshot-thumb-wrapper"
                            >
                                <img
                                    src={url}
                                    alt="Style screenshot"
                                    className="style-screenshot-thumb"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleRemoveScreenshot(url)}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <label>
                    Description
                    <textarea
                        name="description"
                        value={form.description}
                        onChange={handleFormChange}
                    />
                </label>

                <label>
                    <input
                        type="checkbox"
                        name="active"
                        checked={form.active}
                        onChange={handleFormChange}
                    />
                    Active
                </label>

                <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                    <button type="submit">Save Style</button>
                    <button type="button" onClick={handleResetForm}>
                        Clear Form
                    </button>
                </div>
            </form>

            <h3>Existing Styles</h3>
            {styles.map((s) => (
                <div key={s.id} className="transaction-item">
                    <strong>{s.name || s.id}</strong>
                    <p>ID: {s.id}</p>
                    <p>Theme: {s.themeKey}</p>
                    <p>Active: {String(s.active)}</p>

                    <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                        <button onClick={() => handleEditStyle(s)}>
                            Edit
                        </button>
                        <button onClick={() => handleDeleteStyle(s.id)}>
                            Delete
                        </button>
                    </div>
                </div>
            ))}

            <h3>Recent Style Purchases</h3>
            {transactions.map((t) => (
                <div key={t.id} className="transaction-item">
                    <p>
                        <strong>{t.styleId}</strong> — {t.status}
                    </p>
                    <p>Buyer: {t.buyer}</p>
                    {t.txSignature && (
                        <p>
                            Tx:{" "}
                            <a
                                href={`https://solscan.io/tx/${t.txSignature}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {t.txSignature}
                            </a>
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
}

export default AdminDashboard;
